import React, { useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import { useAppDispatch } from "src/app-redux/hooks";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { setisDirtyValue } from "src/app-redux/configuration/configurationSlice";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },
  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm {
  id: number;
  consignee: string;
  ntnNumber: string;
  consigneeAddress: string;
  isActive: boolean;
}

interface Props {
  handleSubmit: (formData: IForm) => Promise<void>;
  updateSubmit: (formData: IForm) => Promise<void>;
  loading?: any;
  tableRecord?: any;
}

export function InputFields({
  handleSubmit,
  updateSubmit,
  loading,
  tableRecord,
}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [isEditMode, setisEditMode] = React.useState(false);
  const [submitCounter, setSubmitCounter] = useState<any>(0);
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const validationSchema = object({
    consignee: string()
      .required("This field is required")
      .max(100, "Maximum 100 characters are allowed."),
    ntnNumber: string()
      .required("This field is required")
      .max(50, "Maximum 50 characters are allowed."),
    consigneeAddress: string().max(200, "Maximum 200 characters are allowed."),
  });

  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      id: 0,
      isActive: false,
      consignee: "",
      ntnNumber: "",
      consigneeAddress: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, actions) => {
      if (isEditMode) updateSubmit(values);
      else {
        formik.setFieldValue("id", 0);
        handleSubmit(values);
      }
      actions.setSubmitting(false);
      actions.resetForm();
      setisEditMode(false);
      setIsDirtyValue(false);
      setSelectedRecord(null);
    },
  });

  const handleUpdateClick = (params: any) => {
    setSelectedRecord(params);
    formik.setFieldValue("consignee", params?.consignee || "");
    formik.setFieldValue("ntnNumber", params?.ntnNumber || "");
    formik.setFieldValue("consigneeAddress", params?.consigneeAddress || "");
    formik.setFieldValue("id", params?.id);
    formik.setFieldValue("isActive", params?.isActive ?? false);
    setisEditMode(true);
  };

  useEffect(() => {
    if (submitCounter) formik.submitForm();
  }, [submitCounter]);

  useEffect(() => {
    if (tableRecord.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirtyValue(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column
      );
      setDisplayColumns(transformedColumns);
    }
  }, [tableRecord]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  function handleValueChange(e: any) {
    formik.handleChange(e);
    e.target.value ? setIsDirtyValue(true) : setIsDirtyValue(false);
  }

  function setIsDirtyValue(val: boolean) {
    setIsDirty(val);
    dispatch(setisDirtyValue(val));
  }

  return (
    <>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          <Grid className={classes.UserMainPanel}>
            <Grid
              container
              className={classes.mainConainer}
              direction="row"
              alignItems="center"
              spacing={2}
            >
              <Grid container item xs={12} md={12} lg={12} spacing={2}>
                <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                  <Box>
                    <TextField
                      className="customTextField"
                      required
                      size="small"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Consignee"
                      name="consignee"
                      placeholder="Enter Consignee"
                      disabled={false}
                      value={formik.values.consignee}
                      onChange={handleValueChange}
                      error={formik.touched.consignee && Boolean(formik.errors.consignee)}
                      helperText={formik.touched.consignee && formik.errors.consignee}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                  <Box>
                    <TextField
                      className="customTextField"
                      size="small"
                      required
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="NTN Number"
                      name="ntnNumber"
                      placeholder="Enter NTN Number"
                      disabled={false}
                      value={formik.values.ntnNumber}
                      onChange={handleValueChange}
                      error={formik.touched.ntnNumber && Boolean(formik.errors.ntnNumber)}
                      helperText={formik.touched.ntnNumber && formik.errors.ntnNumber}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                  <Box>
                    <TextField
                      className="customTextField"
                      size="small"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Consignee Address"
                      name="consigneeAddress"
                      placeholder="Enter Consignee Address"
                      disabled={false}
                      value={formik.values.consigneeAddress}
                      onChange={handleValueChange}
                      error={
                        formik.touched.consigneeAddress && Boolean(formik.errors.consigneeAddress)
                      }
                      helperText={formik.touched.consigneeAddress && formik.errors.consigneeAddress}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} md={2} lg={1}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formik.values.isActive)}
                          onChange={formik.handleChange}
                          name="isActive"
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} md={2} lg={2}>
                  <Box mb={2} mt={1} style={{ float: "right" }}>
                    <CommonButton
                      isGreen
                      cssClass="customSubmitBtn"
                      disable={
                        selectedRecord
                          ? formik.values.consignee === selectedRecord.consignee &&
                            formik.values.ntnNumber === selectedRecord.ntnNumber &&
                            formik.values.consigneeAddress === selectedRecord.consigneeAddress &&
                            formik.values.isActive === selectedRecord.isActive
                          : JSON.stringify(formik.values) === JSON.stringify(formik.initialValues)
                      }
                      onClick={() => setSubmitCounter((prev: any) => prev + 1)}
                    >
                      {isEditMode ? "Update" : "Add"}
                    </CommonButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
      <CommonMaterialGrid
        defaultPerPageRecord={10}
        height={400}
        loader={false}
        rows={tableRecord || []}
        columns={displayColumns}
      />
    </>
  );
}
